import { useMemo } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import { Box, useMediaQuery } from '@mui/material';

// project import
import useConfig from 'hooks/useConfig';
import Search from './Search';
import Profile from './Profile';
import Localization from './Localization';
import LeaksNotification from './LeaksNotification';
import MobileSection from './MobileSection';
import MegaMenuSection from './MegaMenuSection';

// hooks
import useAuth from 'hooks/useAuth';
import useDynamicConfig from 'hooks/useDynamicConfig';
import useEnums from 'hooks/useEnum';
import useSkipCompanyQuery from 'hooks/useSkipCompanyQuery';

// store
import { useGetCompanyQuery } from 'store/reducers/api/company';

// ==============================|| HEADER - CONTENT ||============================== //

const HeaderContent = () => {
  const { activeRole } = useAuth();
  const { i18n } = useConfig();
  const { UserRolesEnum } = useEnums();
  const { SYSTEM_ROLES, COMPANY_ROLES, PROPERTY_ROLES } = useDynamicConfig();
  const skipCompanyQuery = useSkipCompanyQuery(activeRole);
  const { data: userCompany } = useGetCompanyQuery(activeRole?.company_id!, { skip: skipCompanyQuery });

  const matchesXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const localization = useMemo(() => <Localization />, [i18n]);

  const megaMenu = useMemo(() => <MegaMenuSection />, []);

  const hasPermissionForSearch = useMemo(() => {
    const { user_role_type_id } = activeRole || {};
    if (!user_role_type_id) return false;
    if (user_role_type_id === UserRolesEnum.COMPANY_INSTALLER) return false;

    if (userCompany) {
      return userCompany.company_type_id !== 3;
    }

    return [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES].includes(user_role_type_id);
  }, [activeRole, SYSTEM_ROLES, COMPANY_ROLES, userCompany, UserRolesEnum, PROPERTY_ROLES]);

  const hasPermissionForNotifications = useMemo(() => {
    const { user_role_type_id } = activeRole || {};
    if (!user_role_type_id) return false;
    if (user_role_type_id === UserRolesEnum.COMPANY_INSTALLER) return false;

    if (userCompany) {
      return userCompany.company_type_id !== 3;
    }

    return [...COMPANY_ROLES, ...PROPERTY_ROLES].includes(user_role_type_id);
  }, [activeRole, COMPANY_ROLES, userCompany, UserRolesEnum, PROPERTY_ROLES]);

  return (
    <>
      {!matchesXs && (hasPermissionForSearch ? <Search /> : <Box sx={{ width: '100%', ml: { xs: 0, md: 1 } }} />)}
      {!matchesXs && megaMenu}
      {!matchesXs && localization}
      {matchesXs && <Box sx={{ width: '100%', ml: 1 }} />}
      {hasPermissionForNotifications && (
        <>
          <LeaksNotification />
        </>
      )}
      {!matchesXs && <Profile />}
      {matchesXs && <MobileSection />}
    </>
  );
};

export default HeaderContent;
